import { useEffect, useState } from "react";

import { Badge, Card, FontIcon } from "@ezmid/components-library";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import { LessonContentCard } from "./LessonContentCard";
import { useUpdateLessonByIdMutation } from "../../../api/lesson-update.generated";
import {
  useGetBackOfficeGetLessonUsersQuery,
  useGetBackOfficeGetLessonQuery,
} from "../../../api/lesson.generated";
import { UsersTable } from "../../../components/tables/UsersTable";
import { Optional, ParamId } from "../../../enums/types";
import { useUploadMedia } from "../../../hooks";
import { useGetImage } from "../../../hooks/useGetImage";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { PageLoader } from "../../loader/PageLoader";
import { AddLessonContentModal } from "../molecules/AddLessonContentModal";
import { LessonDetailForm } from "../molecules/LessonDetailForm";

type FormValues = {
  lessonNameEN: string;
  lessonNameDE: string;
  textEn: string;
  textDe: string;
  order: string;
  isFree: boolean;
  status: string;
};

type Props = {
  isCreateContentModalOpen: boolean;
  onCloseCreateContentModal: () => void;
};

export function LessonPageContent({
  isCreateContentModalOpen,
  onCloseCreateContentModal,
}: Props) {
  const { t } = useTranslation();
  const { id }: ParamId = useParams();
  const [lessonUpdate] = useUpdateLessonByIdMutation();

  const FormSchema = yup.object().shape({
    lessonNameEN: yup.string().required(t("form.lesson.name.required")),
    textEn: yup.string(),
    textDe: yup.string(),
    order: yup.string(),
  });

  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);
  const [lessonImageId, setLessonImageId] = useState<string>();

  const {
    isFetching: isFetchingUserList,
    currentData: userListData,
    isLoading: isUserListLoading,
  } = useGetBackOfficeGetLessonUsersQuery({ id: id || "" });

  const {
    isFetching,
    currentData,
    isError,
    isSuccess,
    isLoading: isLessonLoading,
    refetch,
  } = useGetBackOfficeGetLessonQuery(
    {
      id: id || "",
    },
    {
      refetchOnMountOrArgChange: true,
      skip: isUserListLoading,
    }
  );

  const [imageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { isImageDownloading } = useGetImage(uploadedMediaId || lessonImageId);
  const enTranslations = currentData?.translations?.find(
    ({ language }) => language === "en"
  );
  const deTranslations = currentData?.translations?.find(
    ({ language }) => language === "de"
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      lessonNameEN: enTranslations?.name,
      lessonNameDE: deTranslations?.name,
      textEn: enTranslations?.text,
      textDe: deTranslations?.text,
      order: currentData?.order?.toString(),
      isFree: currentData?.isFree ?? false,
      status: currentData?.status || "ls-draft",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  useEffect(
    function resetFormDataAfterFetch() {
      if (!isFetching && !!currentData) {
        const enTranslation = currentData?.translations?.find(
          ({ language }) => language === "en"
        );
        const deTranslation = currentData?.translations?.find(
          ({ language }) => language === "de"
        );

        reset({
          lessonNameEN: enTranslation?.name,
          lessonNameDE: deTranslation?.name,
          textEn: enTranslation?.text,
          textDe: deTranslation?.text,
          order: currentData?.order?.toString(),
          isFree: currentData?.isFree ?? false,
          status: currentData?.status || "ls-draft",
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentData]
  );

  useEffect(
    function getLessonsImageAfterUserDataFetch() {
      if (!isFetching && !isError && isSuccess) {
        currentData?.titleImage?.id &&
          setLessonImageId(currentData?.titleImage?.id);
        setIsDirtyFromImage(false);
      }
    },
    [currentData, isFetching, isError, isSuccess]
  );

  const onSubmit = () => {
    const {
      lessonNameEN,
      lessonNameDE,
      textDe,
      textEn,
      order,
      isFree,
      status,
    } = getValues();
    setIsDirtyFromImage(false);
    lessonUpdate({
      id: currentData?.id || "",
      lessonUpdateRequest: {
        order: parseInt(order, 10),
        translations: [
          {
            language: "en",
            name: lessonNameEN,
            text: textEn,
          },
          {
            language: "de",
            name: lessonNameDE,
            text: textDe,
          },
        ],
        titleImageID: uploadedMediaId || currentData?.titleImage?.id,
        isFree,
        status: status as any,
      },
    })
      .unwrap()
      .then(() => {
        refetch();
        toast.success(t("app.lesson.edit.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.lesson.edit.failed"));
        }
      });
  };

  const users = userListData?.items?.map((item) => item.user);
  const isFormDirty = isDirty || isDirtyFromImage;
  const isImageLoading = isImageDownloading || isMediaUploading;
  const isSubmitButtonDisabled = !isFormDirty || isImageLoading || isFetching;
  const isPageLoading = isLessonLoading || isUserListLoading;

  return isPageLoading || !currentData ? (
    <PageLoader />
  ) : (
    <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-2 w-full min-w-full">
        <div className="flex flex-col gap-4">
          <Card
            shadows="xl"
            size="fill"
            headerText={t("course.lesson.information")}
            headerIcon={
              <FontIcon
                marginTop={2}
                customIcon={<FontAwesomeIcon size="lg" icon={faInfoCircle} />}
              />
            }
            headerItem={
              <Badge variant="pill">{t("course.lesson.information")}</Badge>
            }
          >
            <LessonDetailForm
              control={control}
              errors={errors}
              handleSubmit={handleSubmit(onSubmit)}
              onSumbit={onSubmit}
              isSubmitButtonDisabled={isSubmitButtonDisabled}
              hasSubmitButton
              lessonId={currentData?.id}
              formResetKey={`${currentData?.id}-${currentData?.updatedAt}`}
            />
          </Card>
          <UsersTable
            isFetching={isFetchingUserList}
            users={users || []}
            headerText={t("course.lesson.users")}
          />
        </div>
        <div className="flex flex-col gap-4">
          <LessonContentCard
            lessonContents={currentData?.contents || []}
            refetch={refetch}
          />
        </div>
      </div>
      <AddLessonContentModal
        isModalOpen={isCreateContentModalOpen}
        onModalClose={onCloseCreateContentModal}
        refetch={refetch}
      />
    </>
  );
}
